/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import * as MsDyn365 from '@msdyn365-commerce/core';
import {
    getPayloadObject,
    getTelemetryAttributes,
    getTelemetryObject,
    INodeProps,
    // isMobile,
    ITelemetryContent,
    // VariantType
} from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IVideoPlayerAdditionalContentViewProps, videoPlayerAdditionalContent } from './components/additional-content';
import { ILinksData, IVideoPlayerMobilityConfig, IVideoPlayerMobilityProps } from './video-player-mobility.props.autogenerated';

export interface IVideoPlayerViewProps extends IVideoPlayerMobilityProps<{}> {
    player: React.ReactNode;
    title?: React.ReactNode;
    text?: React.ReactNode;
    links?: React.ReactNode;
    detailsContainer?: INodeProps;
    detailsContainerCta?: INodeProps;
    additionalContent?: IVideoPlayerAdditionalContentViewProps;
    handleText?(): void;
    handleParagraph?(): void;
    handleAdditionalText?(): void;
    handleAdditionalParagraph?(): void;
    handleLink?(): void;
    handleAdditionalLink?(): void;
}

/**
 *
 * VideoPlayer component.
 * @extends {React.PureComponent<IVideoPlayerMobilityProps<IVideoPlayerMobilityData>>}
 */
class VideoPlayer extends React.PureComponent<IVideoPlayerMobilityProps<IVideoPlayerMobilityConfig>> {
    private readonly telemetryContent: ITelemetryContent;

    // private readonly isMobileDevice: boolean;

    public constructor(props: IVideoPlayerMobilityProps<IVideoPlayerMobilityConfig>) {
        super(props);
        // this.isMobileDevice = isMobile({ variant: VariantType.Viewport, context: this.props.context.request }) === 'xs';
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }

    public render(): JSX.Element {
        const video: MsDyn365.IVideoData = this.props.config.video;
        let videolink = video._links && video._links.binaryReferences && video._links.binaryReferences[0].clientHref;
        let thumbnail = video.thumbnail?.src;
        
        const viewProps = {
            // Context for telemetry and raw data for C1 to do customization
            // Including computed data, state data and methods
            ...this.props,
            player: (
                <video 
                        width="100%" 
                        src={videolink} 
                        controls
                        poster={thumbnail}
                    >
                Sorry, your browser does not support the video tag.
            </video>
                
            ),
            title: this._getVideoDetailsHeading(),
            text: this._getVideoDetailsParagraph(),
            links: this._getVideoDetailsLinks(),
            detailsContainer: { className: 'ms-video-player__details' },
            detailsContainerCTA: { className: 'ms-video-player__details__cta' },
            additionalContent: videoPlayerAdditionalContent({
                requestContext: this.props.context.request,
                additionalContent: this.props.config.additionalContent,
                handleAdditionalTextChange: this.handleAdditionalTextChange,
                handleAdditionalParagraphChange: this.handleAdditionalParagraphChange,
                handleAdditionalLinkTextChange: this.handleAdditionalLinkTextChange
            }),
            handleText: this.handleTextChange,
            handleParagraph: this.handleParagraphChange,
            handleAdditionalText: this.handleAdditionalTextChange,
            handleAdditionalParagraph: this.handleAdditionalParagraphChange,
            handleLink: this.handleLinkTextChange,
            handleAdditionalLink: this.handleAdditionalTextChange
        };
        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Handle link text change.
     * @param linkIndex - The link index.
     * @param event - The dialog that is allowed to remain open.
     */
    public handleLinkTextChange(linkIndex: number, event: MsDyn365.ContentEditableEvent): void {
        if (this.props.config.links && this.props.config.links[Number(linkIndex)]) {
            this.props.config.links[Number(linkIndex)].linkText = event.target.value;
        }
    }

    /**
     * Handle text change.
     * @param event - The dialog that is allowed to remain open.
     */
    public handleTextChange = (event: MsDyn365.ContentEditableEvent): void => {
        this.props.config.heading!.text = event.target.value;
    };

    /**
     * Handle paragraph change.
     * @param event - The dialog that is allowed to remain open.
     */
    public handleParagraphChange = (event: MsDyn365.ContentEditableEvent): void => {
        this.props.config.paragraph = event.target.value;
    };

    /**
     * Handle additional text change.
     * @param event - The dialog that is allowed to remain open.
     */
    public handleAdditionalTextChange(event: MsDyn365.ContentEditableEvent): void {
        if (this.props.config.additionalContent) {
            this.props.config.additionalContent.heading = event.target.value;
        }
    }

    /**
     * Handle additional link text change.
     * @param linkIndex - The link index.
     * @param event - The dialog that is allowed to remain open.
     */
    public handleAdditionalLinkTextChange(linkIndex: number, event: MsDyn365.ContentEditableEvent): void {
        const additionalContent = this.props.config.additionalContent ?? {};
        if (additionalContent.links) {
            additionalContent.links[Number(linkIndex)].linkText = event.target.value;
        }
    }

    /**
     * Handle additional paragraph change.
     * @param event - The dialog that is allowed to remain open.
     */
    public handleAdditionalParagraphChange(event: MsDyn365.ContentEditableEvent): void {
        if (this.props.config.additionalContent) {
            this.props.config.additionalContent.subtext = event.target.value;
        }
    }

    /**
     * Get video details links.
     * @returns - React node.
     */
    private _getVideoDetailsLinks(): React.ReactNode[] | undefined {
        const { links } = this.props.config;
        const payLoad = getPayloadObject('click', this.telemetryContent, '');

        return links?.map((linkData: ILinksData, index: number) => {
            payLoad.contentAction.etext = linkData.linkText;
            const attributes = getTelemetryAttributes(this.telemetryContent, payLoad);
            const linkId = index;
            const editableLink: MsDyn365.ILinksData = {
                ariaLabel: linkData.ariaLabel,
                className: 'ms-video-player__details__cta__link',
                linkText: linkData.linkText,
                linkUrl: linkData.linkUrl.destinationUrl,
                openInNewTab: linkData.openInNewTab,
                role: 'button',
                additionalProperties: attributes
            };

            return (
                <MsDyn365.Link
                    key={linkId}
                    link={editableLink}
                    editProps={{
                        /**
                         * Handle additional paragraph change.
                         * @param event - The keyboard event.
                         */
                        onTextChange: event => {
                            this.handleLinkTextChange(index, event);
                        },
                        requestContext: this.props.context.request
                    }}
                />
            );
        });
    }

    private _getVideoDetailsHeading(): React.ReactNode | undefined {
        const { heading } = this.props.config;

        return (
            heading && (
                <MsDyn365.Text
                    className='ms-video-player__details__title'
                    tag={heading.tag ?? 'h1'}
                    text={heading.text}
                    editProps={{ onEdit: this.handleTextChange, requestContext: this.props.context.request }}
                />
            )
        );
    }

    private _getVideoDetailsParagraph(): React.ReactNode | undefined {
        const { paragraph } = this.props.config;

        return (
            paragraph && (
                <MsDyn365.RichTextComponent
                    text={paragraph}
                    className='ms-video-player__details__text'
                    editProps={{ onEdit: this.handleParagraphChange, requestContext: this.props.context.request }}
                />
            )
        );
    }
}

export default VideoPlayer;
